// Helpers for the redesigned Performance views (summary cards, Monthly
// Performance trend table and the MUI charts grid). Ported verbatim from the
// senior's startup-web (views/performance/utils/performanceTrends.js).
//
// The metrics table endpoint returns pre-formatted display strings, while the
// charts endpoint (FetchAllGraphs) returns the raw numeric values per month.
// Trends (month-over-month change) are therefore computed here on the client
// from the raw charts data.
//
// INVESTOR NOTE: the only change from the senior's file is that the generic
// unit "Currency" is treated like INR (₹) — investor graph/config data can carry
// "Currency" as well as "INR"/"USD". Everything else is identical.

const CURRENCY_UNITS = ["INR", "USD", "Currency"];

export const isCurrencyUnit = (unit) => CURRENCY_UNITS.includes(unit);

// At most 2 decimal places, and no trailing zeros - so 6.630043029785157 reads
// as 6.63 while 25 stays 25 rather than becoming "25.00". Number() drops the
// zeros that toFixed pads on.
const roundTo2 = (value) => Number(value.toFixed(2));

// Mirrors the backend `formatValue` helper so summary cards / chart chips read
// identically to the table cells.
export const formatMetricValue = (value, unit) => {
    if (value === null || value === undefined || value === "") return "";
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numericValue)) return String(value);

    const isNegative = numericValue < 0;
    const abs = Math.abs(numericValue);
    const sign = isNegative ? "-" : "";

    if (unit === "INR" || unit === "USD" || unit === "Currency") {
        const symbol = unit === "USD" ? "$" : "₹";
        const currency = unit === "USD" ? "USD" : "INR";
        return `${sign}${symbol}${formatCompactCurrency(abs, currency)}`;
    }
    if (unit === "Percentage") return `${sign}${roundTo2(abs)}%`;
    if (unit === "Count") return `${sign}${formatCompactCurrency(abs, "USD")}`;
    if (unit === "Months") return `${sign}${roundTo2(abs)}`;
    return String(roundTo2(numericValue));
};

const formatCompactCurrency = (value, unit) => {
    if (unit === "INR") {
        if (value >= 1_00_00_000) return (value / 1_00_00_000).toFixed(1) + " Cr";
        if (value >= 1_00_000) return (value / 1_00_000).toFixed(1) + " L";
    } else if (unit === "USD") {
        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
        if (value >= 1_000) return (value / 1_000).toFixed(1) + "K";
    }
    return roundTo2(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

// Build a lookup of key_slug -> { name, unit, points } where points are the
// chronologically-ascending raw values coming from the charts endpoint.
export const buildSeriesMap = (graphData) => {
    const map = {};
    if (!Array.isArray(graphData)) return map;
    graphData.forEach((chart) => {
        const slug = chart?.key_slug;
        if (!slug) return;
        map[slug] = {
            name: chart?.name,
            unit: chart?.Unit,
            points: (chart?.data || []).map((point) => ({
                month: point?.month,
                value: point?.value === "" || point?.value === null || point?.value === undefined
                    ? null
                    : Number(point?.value),
            })),
        };
    });
    return map;
};

// Compute the change between the current and previous value.
export const computeChange = ({ current, previous, unit }) => {
    if (current === null || current === undefined || previous === null || previous === undefined) {
        return null;
    }
    const diff = current - previous;
    const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";

    if (unit === "Percentage") {
        return { text: `${Math.abs(diff).toFixed(1)}pp`, direction };
    }
    if (previous === 0) return null;
    const pct = (diff / Math.abs(previous)) * 100;
    return { text: `${Math.abs(pct).toFixed(1)}%`, direction };
};

// Trend for a metric at a specific month (used per-cell in the table).
export const changeForMonth = (series, month) => {
    if (!series) return null;
    const idx = series.points.findIndex((p) => p.month === month);
    if (idx <= 0) return null;
    return computeChange({
        current: series.points[idx].value,
        previous: series.points[idx - 1].value,
        unit: series.unit,
    });
};

// Latest value + month-over-month trend for a metric (used by summary cards).
export const latestSummary = (series) => {
    if (!series || series.points.length === 0) return null;
    const points = series.points;
    const last = points[points.length - 1];
    const prev = points.length > 1 ? points[points.length - 2] : null;
    return {
        latest: last?.value,
        latestMonth: last?.month,
        prevMonth: prev?.month,
        change: prev
            ? computeChange({ current: last?.value, previous: prev?.value, unit: series.unit })
            : null,
    };
};

// Ordered list of configured metrics -> summary card view models.
export const buildSummaryCards = ({ savedConfig, seriesMap, tableData }) => {
    if (!Array.isArray(savedConfig)) return [];
    const latestRow = Array.isArray(tableData) ? tableData[0] : null; // rows sorted most-recent first
    return savedConfig
        .slice()
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((metric) => {
            const slug = metric?.keySlug;
            const series = seriesMap[slug];
            const summary = latestSummary(series);
            const preformatted = latestRow ? latestRow[slug] : "";
            const value = preformatted && preformatted !== ""
                ? preformatted
                : summary
                    ? formatMetricValue(summary.latest, metric?.unit)
                    : "";
            return {
                slug,
                name: metric?.key,
                unit: metric?.unit,
                value: value || "",
                change: summary?.change || null,
                prevMonth: summary?.prevMonth || null,
                points: (series?.points || []).map((p) => (p.value === null ? 0 : p.value)),
            };
        });
};

export const unitLabel = (unit) => {
    if (!unit) return "";
    if (unit === "Percentage") return "(%)";
    return `(${unit})`;
};
